import { isEqual } from "lodash";
import validate from "validate.js";
import { UnavailableEnvironmentIndexError } from "../../IndexEntry";
import { RichString } from "../../RichString";
import type { ComputedView } from "../../database/ComputedView";
import type { GlobalAction } from "../../database/behaviour/GlobalAction";
import { type AnyEnvironmentIndex, getEnvironmentIndex } from "../../getEnvironmentIndex";
import { getAppSettings, getGeneratingRoot, getNamespacedApiIdentifier } from "../../utils";
import type { BaseOptions } from "../Constraints";
import type { IStateTreeNode } from "@gadgetinc/mobx-quick-tree";
import { camelizedNamespacedApiId } from "../../naming/APIElementNamer";
import type { DataModel } from "../../database/DataModel";
import { isExcludedNamespaceSegment } from "@gadgetinc/conventions";

export function withEnvironmentIndex<T extends IStateTreeNode>(node: T, f: (index: AnyEnvironmentIndex) => any) {
  try {
    if (!getAppSettings(node).frameworkFeatures.supportNamespace) return;
  } catch (_error) {
    return;
  }

  let index;
  try {
    index = getEnvironmentIndex(getGeneratingRoot(node));
  } catch (e) {
    if (e instanceof UnavailableEnvironmentIndexError) {
      return; // don't validate uniqueness when the environment index is unavailable
    } else {
      throw e;
    }
  }

  return f(index);
}

validate.validators.noNamespacedIdentifierCollision = <T extends GlobalAction | ComputedView | DataModel>(
  namespacedApiIdentifier: string,
  options: BaseOptions<T> & { subject: "Action" | "View" | "Model"; filter?: (node: T) => boolean },
  key: keyof T & string,
  node: T
) => {
  if (options.filter && !options.filter(node)) return;
  return withEnvironmentIndex(node, (index: AnyEnvironmentIndex) => {
    for (const model of index.modelsWithoutProblemsData) {
      if (node.type == "DataModel" && model.key === node.key) continue; // skip self
      const result = checkNamespacedIdentifierConflicts(options.subject, node, "model", model);
      if (result) return result;
    }
    for (const action of index.globalActionsWithoutProblemsData) {
      // Cannot use key because global action key excludes ignored namespace segments
      if (node.type == "GlobalAction" && action.sourceFilePath === node.sourceFilePath) continue; // skip self
      const result = checkNamespacedIdentifierConflicts(options.subject, node, "action", action);
      if (result) return result;
    }
    for (const view of index.globalViewsWithoutProblemsData) {
      if (node.type == "ComputedView" && view.sourceFilePath === node.sourceFilePath) continue; // skip self
      const result = checkNamespacedIdentifierConflicts(options.subject, node, "view", view);
      if (result) return result;
    }
  });
};

type namespacedIdentifier = {
  namespace: string[];
  apiIdentifier: string;
};

function checkNamespacedIdentifierConflicts(
  subjectType: string,
  subject: namespacedIdentifier,
  objectType: string,
  object: namespacedIdentifier
) {
  const subjectNamespace = subject.namespace.filter((segment) => !isExcludedNamespaceSegment(segment));
  const objectNamespace = object.namespace.filter((segment) => !isExcludedNamespaceSegment(segment));
  const subjectNamespacedId = getNamespacedApiIdentifier(subjectNamespace, subject.apiIdentifier);
  const fullSubjectNamespacedId = getNamespacedApiIdentifier(subject.namespace, subject.apiIdentifier);
  const objectNamespacedId = getNamespacedApiIdentifier(objectNamespace, object.apiIdentifier);
  const fullObjectNamespacedId = getNamespacedApiIdentifier(object.namespace, object.apiIdentifier);

  // Check direct collision
  if (objectNamespacedId === subjectNamespacedId) {
    return (_value: string) =>
      `^${subjectType} name ${RichString.text(fullSubjectNamespacedId)} collides with ${objectType} name ${RichString.text(
        fullObjectNamespacedId
      )}`;
  }

  // Check namespace collision
  const objectNamespacePath = objectNamespace.join("/");
  if (objectNamespacePath.startsWith(subjectNamespacedId))
    return (_value: string) =>
      `^${subjectType} name ${RichString.text(fullSubjectNamespacedId)} collides with ${objectType} namespace ${RichString.text(
        objectNamespacePath
      )}`;

  // Check reverse namespace collision
  const subjectNamespacePath = subjectNamespace.join("/");
  if (subjectNamespacePath.startsWith(objectNamespacedId))
    return (_value: string) =>
      `^${subjectType} namespace ${RichString.text(subjectNamespacePath)} collides with ${objectType} name ${RichString.text(
        fullObjectNamespacedId
      )}`;

  // Check for structural conflicts. Ex: object `a/b/c` and subject `aB/c`
  // These will cause collisions in GraphQLSchema
  if (
    isEqual(
      camelizedNamespacedApiId(subjectNamespace, subject.apiIdentifier),
      camelizedNamespacedApiId(objectNamespace, object.apiIdentifier)
    )
  ) {
    return (_value: string) =>
      `^${subjectType} namespace/id ${RichString.text(fullSubjectNamespacedId)} collides structurally with ${objectType} ${RichString.text(
        fullObjectNamespacedId
      )}`;
  }

  return;
}
